import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CalendarDate, CampaignEvent, CharacterEvent, createReferenceToNode, daysPerYear, GalaxyEvent, Location, useAppContext, } from '@datapad/data-model';
import { Add, Delete } from '@mui/icons-material';
import { Autocomplete, Box, Button, Chip, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, List, ListItem, ListItemText, MenuItem, Select, TextField, Typography, } from '@mui/material';
import React from 'react';
const EVENT_TYPE_LABELS = {
    galaxy: 'Galaxy Event',
    campaign: 'Campaign Event',
    character: 'Character Event',
};
export function EventCreationForm(props) {
    const { initialDate, onSubmit, onCancel } = props;
    const appContext = useAppContext();
    const [eventType, setEventType] = React.useState('campaign');
    const [title, setTitle] = React.useState('');
    const [year, setYear] = React.useState(initialDate?.year ?? '');
    const [day, setDay] = React.useState(initialDate?.day ?? '');
    const [description, setDescription] = React.useState('');
    const [locationName, setLocationName] = React.useState('');
    const [locationUrl, setLocationUrl] = React.useState('');
    const [selectedCharacters, setSelectedCharacters] = React.useState([]);
    const [factions, setFactions] = React.useState([]);
    const [selectedCampaign, setSelectedCampaign] = React.useState(null);
    const allCharacters = React.useMemo(() => appContext.getAllCharacters(), [appContext]);
    const allCampaigns = React.useMemo(() => appContext.getAllCampaigns(), [appContext]);
    React.useEffect(() => {
        if (!selectedCampaign && allCampaigns.length > 0) {
            if ('campaign' in appContext && appContext.campaign !== undefined) {
                setSelectedCampaign(appContext.getCampaign(appContext.campaign));
            }
            else {
                setSelectedCampaign(allCampaigns[0]);
            }
        }
    }, [selectedCampaign, allCampaigns, appContext]);
    const isValidYear = typeof year === 'number' && Number.isInteger(year) && Number.isFinite(year);
    const isValidDay = typeof day === 'number' && Number.isInteger(day) && day >= 0 && day < daysPerYear;
    const hasTitle = title.trim().length > 0;
    const hasCampaign = eventType !== 'campaign' || selectedCampaign !== null;
    const hasFactions = eventType !== 'galaxy' || factions.length > 0;
    const hasCharacters = eventType !== 'character' || selectedCharacters.length > 0;
    const canSubmit = isValidYear && isValidDay && hasTitle && hasCampaign && hasFactions && hasCharacters;
    const handleSubmit = React.useCallback(() => {
        if (!canSubmit)
            return;
        const date = CalendarDate.create(year, day);
        const characterRefs = selectedCharacters.map((char) => createReferenceToNode(char));
        const location = locationName.trim()
            ? new Location({
                name: locationName.trim(),
                resourceUrl: locationUrl.trim() || undefined,
            })
            : undefined;
        let event;
        switch (eventType) {
            case 'galaxy':
                event = new GalaxyEvent({
                    title: title.trim(),
                    date,
                    description: description.trim() || undefined,
                    location,
                    characters: characterRefs,
                    involvedFactions: factions,
                });
                break;
            case 'campaign':
                event = new CampaignEvent({
                    title: title.trim(),
                    date,
                    description: description.trim() || undefined,
                    location,
                    characters: characterRefs,
                    campaign: createReferenceToNode(selectedCampaign),
                });
                break;
            case 'character':
                event = new CharacterEvent({
                    title: title.trim(),
                    date,
                    description: description.trim() || undefined,
                    location,
                    characters: characterRefs,
                });
                break;
        }
        onSubmit(event);
    }, [
        canSubmit,
        eventType,
        title,
        year,
        day,
        description,
        locationName,
        locationUrl,
        selectedCharacters,
        factions,
        selectedCampaign,
        onSubmit,
    ]);
    return (_jsxs(_Fragment, { children: [_jsx(DialogTitle, { children: "Create New Event" }), _jsx(DialogContent, { dividers: true, children: _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }, children: [_jsxs(FormControl, { fullWidth: true, size: "small", children: [_jsx(InputLabel, { id: "event-type-label", children: "Event Type" }), _jsx(Select, { labelId: "event-type-label", value: eventType, label: "Event Type", onChange: (e) => setEventType(e.target.value), children: Object.entries(EVENT_TYPE_LABELS).map(([value, label]) => (_jsx(MenuItem, { value: value, children: label }, value))) })] }), _jsx(TextField, { label: "Title", value: title, onChange: (e) => setTitle(e.target.value), required: true, fullWidth: true, size: "small", error: !hasTitle, helperText: !hasTitle ? 'Title is required' : undefined }), _jsxs(Box, { sx: { display: 'flex', gap: 2 }, children: [_jsx(TextField, { label: "Year", type: "number", value: year, onChange: (e) => {
                                        const val = e.target.value;
                                        setYear(val === '' ? '' : parseInt(val, 10));
                                    }, required: true, size: "small", error: !isValidYear, helperText: !isValidYear ? 'Must be an integer' : undefined, sx: { flex: 1 } }), _jsx(TextField, { label: "Day of Year", type: "number", value: day, onChange: (e) => {
                                        const val = e.target.value;
                                        setDay(val === '' ? '' : parseInt(val, 10));
                                    }, required: true, size: "small", error: !isValidDay, helperText: !isValidDay ? `Must be 0-${daysPerYear - 1}` : undefined, sx: { flex: 1 }, inputProps: { min: 0, max: daysPerYear - 1 } })] }), _jsx(TextField, { label: "Description", value: description, onChange: (e) => setDescription(e.target.value), multiline: true, rows: 3, fullWidth: true, size: "small" }), _jsxs(Box, { sx: { display: 'flex', gap: 2 }, children: [_jsx(TextField, { label: "Location", value: locationName, onChange: (e) => setLocationName(e.target.value), size: "small", sx: { flex: 1 } }), _jsx(TextField, { label: "Location URL", value: locationUrl, onChange: (e) => setLocationUrl(e.target.value), size: "small", placeholder: "https://...", sx: { flex: 1 } })] }), _jsx(Box, { children: _jsx(Autocomplete, { multiple: true, size: "small", options: allCharacters, getOptionLabel: (char) => char.name, value: selectedCharacters, onChange: (_, newValue) => setSelectedCharacters(newValue), renderInput: (params) => (_jsx(TextField, { ...params, label: "Involved Characters", required: eventType === 'character', error: !hasCharacters, helperText: !hasCharacters
                                        ? 'At least one character is required'
                                        : undefined })), renderTags: (value, getTagProps) => value.map((char, index) => (_createElement(Chip, { ...getTagProps({ index }), key: char.id, label: char.name, size: "small" }))) }) }), eventType === 'galaxy' && (_jsx(FactionListEditor, { factions: factions, onChange: setFactions, error: !hasFactions })), eventType === 'campaign' && (_jsx(Autocomplete, { size: "small", options: allCampaigns, getOptionLabel: (camp) => camp.name, value: selectedCampaign, onChange: (_, newValue) => setSelectedCampaign(newValue), renderInput: (params) => (_jsx(TextField, { ...params, label: "Campaign", required: true, error: !hasCampaign, helperText: !hasCampaign ? 'Campaign is required' : undefined })), disableClearable: allCampaigns.length > 0 }))] }) }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: onCancel, children: "Cancel" }), _jsx(Button, { onClick: handleSubmit, variant: "contained", disabled: !canSubmit, children: "Create Event" })] })] }));
}
function FactionListEditor(props) {
    const { factions, onChange, error } = props;
    const [newFaction, setNewFaction] = React.useState('');
    const handleAdd = React.useCallback(() => {
        const trimmed = newFaction.trim();
        if (trimmed && !factions.includes(trimmed)) {
            onChange([...factions, trimmed]);
            setNewFaction('');
        }
    }, [newFaction, factions, onChange]);
    const handleRemove = React.useCallback((index) => {
        onChange(factions.filter((_, i) => i !== index));
    }, [factions, onChange]);
    const handleKeyDown = React.useCallback((e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAdd();
        }
    }, [handleAdd]);
    return (_jsxs(Box, { children: [_jsxs(Typography, { variant: "subtitle2", color: error ? 'error' : 'text.secondary', gutterBottom: true, children: ["Involved Factions ", error && '(at least one required)'] }), factions.length > 0 && (_jsx(List, { dense: true, disablePadding: true, children: factions.map((faction, index) => (_jsx(ListItem, { disableGutters: true, secondaryAction: _jsx(IconButton, { size: "small", onClick: () => handleRemove(index), "aria-label": "remove", children: _jsx(Delete, { fontSize: "small" }) }), children: _jsx(ListItemText, { primary: faction }) }, `${faction}-${index}`))) })), _jsxs(Box, { sx: { display: 'flex', gap: 1, mt: 1 }, children: [_jsx(TextField, { size: "small", value: newFaction, onChange: (e) => setNewFaction(e.target.value), onKeyDown: handleKeyDown, placeholder: "Add faction", fullWidth: true }), _jsx(IconButton, { size: "small", onClick: handleAdd, disabled: !newFaction.trim(), color: "primary", "aria-label": "add faction", children: _jsx(Add, {}) })] })] }));
}
//# sourceMappingURL=EventCreationForm.js.map